import { useEffect, useState } from "react";

import EmptyState from "@/components/common/EmptyState";
import SectionCard from "@/components/common/SectionCard";
import { Button } from "@/components/ui/button";
import {
  useCreateSnippet,
  useDeleteSnippet,
  useResetStatusDefault,
  useSetStatusDefault,
  useStatusTemplates,
  useUpdateSnippet,
} from "@/hooks/useStatusTemplates";
import { useAuth } from "@/lib/auth";
import type { StatusSnippet } from "@/types/settings.types";

const ADMIN_ROLES = ["org_owner", "brokerage_admin"];

function SnippetRow({
  snippet,
  canEdit,
  onSave,
  onDelete,
  saving,
  deleting,
}: {
  snippet: StatusSnippet;
  canEdit: boolean;
  onSave: (values: { id: string; title: string; body: string }) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  deleting: boolean;
}) {
  const [title, setTitle] = useState(snippet.title);
  const [body, setBody] = useState(snippet.body);

  useEffect(() => {
    setTitle(snippet.title);
    setBody(snippet.body);
  }, [snippet.title, snippet.body]);

  return (
    <li className="rounded-xl border border-dash-border bg-white p-4">
      {canEdit ? (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-dash-ink">
            Name
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-dash-border bg-white px-3 text-sm text-dash-ink"
              maxLength={600}
            />
          </label>
          <label className="block text-sm font-medium text-dash-ink">
            Message
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={3}
              maxLength={600}
              className="mt-1 w-full rounded-md border border-dash-border bg-white p-3 text-sm text-dash-ink"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="dashboardPrimary"
              loading={saving}
              disabled={!title.trim() || !body.trim()}
              onClick={() => onSave({ id: snippet.id, title, body })}
            >
              Save update
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={deleting}
              onClick={() => onDelete(snippet.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm font-semibold text-dash-ink">{snippet.title}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-dash-muted">{snippet.body}</p>
        </div>
      )}
    </li>
  );
}

export default function StatusTemplates({ orgId }: { orgId: string }) {
  const { context } = useAuth();
  const canEdit = ADMIN_ROLES.includes(context?.membership?.role ?? "");
  const { data, isLoading } = useStatusTemplates(orgId);
  const setDefault = useSetStatusDefault(orgId);
  const resetDefault = useResetStatusDefault(orgId);
  const createSnippet = useCreateSnippet(orgId);
  const updateSnippet = useUpdateSnippet(orgId);
  const deleteSnippet = useDeleteSnippet(orgId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");

  if (isLoading) return null;

  const defaults = data?.defaults ?? [];
  const snippets = data?.snippets ?? [];

  return (
    <div className="flex flex-col gap-6">
      <SectionCard title="Stage defaults">
        <p className="mb-4 text-sm text-dash-muted">
          What a client reads when their agent has not written an update. Leave a stage untouched to keep the built-in wording.
        </p>
        <div className="flex flex-col gap-4">
          {defaults.map((item) => (
            <div key={item.slot} className="rounded-xl border border-dash-border bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor={item.slot} className="text-sm font-semibold text-dash-ink">
                  {item.label}
                </label>
                {item.is_override && canEdit && (
                  <Button
                    variant="seamless"
                    type="button"
                    loading={resetDefault.isPending && resetDefault.variables?.slot === item.slot}
                    onClick={() => resetDefault.mutate(
                      { slot: item.slot },
                      { onSuccess: () => setDrafts((current) => {
                        const next = { ...current };
                        delete next[item.slot];
                        return next;
                      }) },
                    )}
                    data-testid={`status-template-reset-${item.slot}`}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
              <textarea
                id={item.slot}
                disabled={!canEdit}
                value={drafts[item.slot] ?? item.body}
                onChange={(event) => setDrafts((current) => ({ ...current, [item.slot]: event.target.value }))}
                rows={3}
                maxLength={600}
                className="mt-2 w-full rounded-lg border border-dash-border bg-white p-3 text-sm text-dash-ink disabled:bg-dash-hover disabled:text-dash-muted"
                data-testid={`status-template-body-${item.slot}`}
              />
              {canEdit && (
                <Button
                  variant="dashboardPrimary"
                  type="button"
                  className="mt-2"
                  loading={setDefault.isPending && setDefault.variables?.slot === item.slot}
                  disabled={!(drafts[item.slot] ?? item.body).trim()}
                  onClick={() => setDefault.mutate({ slot: item.slot, body: drafts[item.slot] ?? item.body })}
                  data-testid={`status-template-save-${item.slot}`}
                >
                  Save
                </Button>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Saved updates">
        <p className="mb-4 text-sm text-dash-muted">
          Reusable messages your agents can drop into a deal update and tailor before sending.
        </p>
        {snippets.length === 0 ? (
          <EmptyState
            compact
            title="No saved updates yet"
            description="Add one and your agents can insert it from any deal."
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {snippets.map((snippet) => (
              <SnippetRow
                key={snippet.id}
                snippet={snippet}
                canEdit={canEdit}
                saving={updateSnippet.isPending && updateSnippet.variables?.id === snippet.id}
                deleting={deleteSnippet.isPending && deleteSnippet.variables?.id === snippet.id}
                onSave={(values) => updateSnippet.mutate(values)}
                onDelete={(id) => deleteSnippet.mutate({ id })}
              />
            ))}
          </ul>
        )}

        {canEdit && (
          <div className="mt-5 rounded-xl border border-dash-border bg-dash-hover p-4">
            <h3 className="text-sm font-semibold text-dash-ink">Add a saved update</h3>
            <div className="mt-3 space-y-3">
              <label className="block text-sm font-medium text-dash-ink">
                Name
                <input
                  value={newTitle}
                  onChange={(event) => setNewTitle(event.target.value)}
                  placeholder="Waiting on appraisal"
                  maxLength={600}
                  className="mt-1 h-10 w-full rounded-md border border-dash-border bg-white px-3 text-sm text-dash-ink"
                />
              </label>
              <label className="block text-sm font-medium text-dash-ink">
                Message
                <textarea
                  value={newBody}
                  onChange={(event) => setNewBody(event.target.value)}
                  placeholder="The appraisal is underway. I’ll share the result as soon as it lands."
                  rows={3}
                  maxLength={600}
                  className="mt-1 w-full rounded-md border border-dash-border bg-white p-3 text-sm text-dash-ink"
                />
              </label>
              <Button
                type="button"
                variant="dashboardPrimary"
                loading={createSnippet.isPending}
                disabled={!newTitle.trim() || !newBody.trim()}
                onClick={() => createSnippet.mutate(
                  { title: newTitle, body: newBody },
                  { onSuccess: () => { setNewTitle(""); setNewBody(""); } },
                )}
              >
                Add saved update
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
